import { useRef, useEffect } from "react"
import { Motion } from "../classes/types"
import { Player } from "../classes/player"
import { Input } from "../classes/input"
import * as BABYLON from "babylonjs"
import { TerrainMaterial } from "babylonjs-materials"
import * as GUI from "babylonjs-gui"
import { XRPointerInteractions } from "./xrPointerInteractions"
import { HandMenu } from "./handMenu"
import { Global } from "../classes/global"

let canvas: HTMLCanvasElement
let lookAt: BABYLON.Vector3
export var engine: BABYLON.Engine
export var scene: BABYLON.Scene
export var xr: BABYLON.WebXRDefaultExperience
let meshMain: BABYLON.Nullable<BABYLON.AbstractMesh>
let light: BABYLON.DirectionalLight
let cameraFree: BABYLON.FreeCamera
let cameraRotate: BABYLON.ArcRotateCamera
let cameraOption = "top"
const cameraDistance = 3.5
const lightX = 2
const lightY = 0
const lightZ = 4
const totalImages = 17
let sceneLoaded = false

export function changeView(option: string) {
	if (cameraOption === option) return
	cameraOption = option

	if (option === "third") {
		cameraFree.attachControl(canvas)
		Input.keysEnabled = true
		Input.toggleSteeringWheel(true)
		scene.activeCamera = cameraFree
		const canvasJoystick = document.getElementById("joystick")
		canvasJoystick!.style.zIndex = "2"
	} else if (option === "top") {
		cameraFree.attachControl(canvas)
		Input.keysEnabled = true
		scene.activeCamera = cameraFree
		cameraFree.position.x = 0
		cameraFree.position.y = 3 - Player.height
		cameraFree.position.z = 9
		lookAt = new BABYLON.Vector3(0, Player.height, 0)
		cameraFree.setTarget(lookAt)
		Input.toggleSteeringWheel(false)
		const canvasJoystick = document.getElementById("joystick")
		canvasJoystick!.style.zIndex = "2"
	} else {
		cameraFree.detachControl()
		Player.centre()
		scene.activeCamera = cameraRotate
		Input.keysEnabled = false
		const canvasJoystick = document.getElementById("joystick")
		canvasJoystick!.style.zIndex = "-1"
	}
}

function screencap() {
	BABYLON.Tools.CreateScreenshotUsingRenderTarget(engine, cameraFree, {
		height: 500,
		width: 2000,
	})
}

function preloadImages(
	onProgress: (value: number) => void,
	loadScene: () => void
) {
	let loaded = 0
	const arrImages = [
		"blue.png",
		"default_top.jpg",
		"Eye_Cornea_DIFFUSE.jpg",
		"Eye_Sclera_DIFFUSE.jpg",
		"Eye_Sclera_OPACITY.jpg",
		"green.png",
		"red.png",
		"skybox_nx.jpg",
		"skybox_ny.jpg",
		"skybox_nz.jpg",
		"skybox_px.jpg",
		"skybox_py.jpg",
		"skybox_pz.jpg",
		"Sphynx_Body_DIFFUSE.jpg",
		"Sphynx_Body_NORMALMAP.jpg",
		"splat.png",
		"top_normal.jpg",
	]
	// 17 images
	arrImages.forEach((image) => {
		let img = new Image()
		img.onload = () => {
			loaded++
			onProgress(loaded)
			if (loaded >= arrImages.length) loadScene()
		}
		img.src = "assets/" + image
	})
}

function updateLight() {
	if (Player.root) {
		light.position.x = Player.root.position.x + lightX
		light.position.y = Player.root.position.y + lightY
		light.position.z = Player.root.position.z + lightZ
	}
}

function updateCamera() {
	const xOffset =
		Math.sin(Player.absoluteRotation - Math.PI / 2) * -1 * cameraDistance
	const zOffset =
		Math.cos(Player.absoluteRotation - Math.PI / 2) * -1 * cameraDistance

	if (Player.root) {
		cameraFree.position.x = Player.root.position.x - xOffset
		cameraFree.position.y = Player.root.position.y + 1.2 - Player.height
		cameraFree.position.z = Player.root.position.z - zOffset
		if (Player.root.position.x === -0) Player.root.position.x = 0
		lookAt = new BABYLON.Vector3(
			Player.root.position.x,
			Player.root.position.y + Player.height + 0.6,
			Player.root.position.z
		)
		cameraFree.setTarget(lookAt)
	}
}

function setupSkyBox() {
	const skybox = BABYLON.MeshBuilder.CreateBox(
		"skyBox",
		{ size: 1000.0 },
		scene
	)
	const skyboxMaterial = new BABYLON.StandardMaterial("skyBox", scene)
	skyboxMaterial.backFaceCulling = false
	skyboxMaterial.reflectionTexture = new BABYLON.CubeTexture(
		"assets/skybox",
		scene
	)
	skyboxMaterial.reflectionTexture.coordinatesMode = BABYLON.Texture.SKYBOX_MODE
	skyboxMaterial.diffuseColor = new BABYLON.Color3(0, 0, 0)
	skyboxMaterial.specularColor = new BABYLON.Color3(0, 0, 0)
	skybox.material = skyboxMaterial
}

function setupShadows() {
	const shadowGenerator = new BABYLON.ShadowGenerator(1024, light)
	shadowGenerator.addShadowCaster(meshMain!, false)
	shadowGenerator.usePercentageCloserFiltering = true
	shadowGenerator.blurKernel = 1
	shadowGenerator.darkness = 0.1
}

function setupWorld() {
	const splatScale = 10
	var terrainMaterial = new TerrainMaterial("terrainMaterial", scene)
	terrainMaterial.mixTexture = new BABYLON.Texture("assets/splat.png", scene)
	terrainMaterial.diffuseTexture1 = new BABYLON.Texture("assets/red.png", scene)
	terrainMaterial.diffuseTexture2 = new BABYLON.Texture(
		"assets/green.png",
		scene
	)
	terrainMaterial.diffuseTexture3 = new BABYLON.Texture(
		"assets/blue.png",
		scene
	)

	terrainMaterial.diffuseTexture1.uScale =
		terrainMaterial.diffuseTexture1.vScale = splatScale
	terrainMaterial.diffuseTexture2.uScale =
		terrainMaterial.diffuseTexture2.vScale = splatScale
	terrainMaterial.diffuseTexture3.uScale =
		terrainMaterial.diffuseTexture3.vScale = splatScale

	const ground = BABYLON.MeshBuilder.CreateGround(
		"ground",
		{ width: 10, height: 10 },
		scene
	)
	ground.material = terrainMaterial
	ground.position = BABYLON.Vector3.Zero()
	ground.receiveShadows = true

	Input.setupKeyboard()
	Input.setupJoystick()
	Input.state = Motion.IDLE
	Input.keysEnabled = true
}

function setupFreeCamera() {
	cameraFree = new BABYLON.FreeCamera(
		"FreeCamera",
		BABYLON.Vector3.Zero(),
		scene
	)
	cameraFree.keysDown =
		cameraFree.keysUp =
		cameraFree.keysLeft =
		cameraFree.keysRight =
			[]
	cameraFree.position.x = 0
	cameraFree.position.y = 3 - Player.height
	cameraFree.position.z = 9
	cameraFree.inertia = 0
	const lookAt = new BABYLON.Vector3(0, Player.height, 0)
	cameraFree.setTarget(lookAt)
	cameraFree.attachControl(canvas)
}

function setupArcRotateCamera() {
	const radius = 1.0
	const beta = 1.4
	const alpha = -Math.PI / 2
	cameraRotate = new BABYLON.ArcRotateCamera(
		"Camera",
		alpha,
		beta,
		radius,
		new BABYLON.Vector3(0, 0, 0),
		scene
	)
	cameraRotate.lowerBetaLimit = cameraRotate.upperBetaLimit = beta
	cameraRotate.lowerRadiusLimit = cameraRotate.upperRadiusLimit = radius
	cameraRotate.panningSensibility = 0
	cameraRotate.minZ = 0.1
	cameraRotate.setTarget(new BABYLON.Vector3(0, 0.2, 0))
	cameraRotate.attachControl(canvas)
}

function setupLighting() {
	const lightHemi = new BABYLON.HemisphericLight(
		"HemiLight",
		new BABYLON.Vector3(0, 1, 0),
		scene
	)
	lightHemi.intensity = 0.6
	light = new BABYLON.DirectionalLight(
		"dir",
		new BABYLON.Vector3(0, -2, Math.PI),
		scene
	)
	light.intensity = 1
	light.shadowMinZ = -56
	light.shadowMaxZ = 50
}

function setupWalls() {
	const yPos = -1.3
	const wallEnd1 = BABYLON.MeshBuilder.CreatePlane(
		"wallEnd1",
		{ size: 50, width: 10, height: 9 },
		scene
	)
	wallEnd1.position.y = yPos
	wallEnd1.position.z = 5
	wallEnd1.checkCollisions = true
	const matWall = new BABYLON.StandardMaterial("matWallEnd", scene)
	matWall.alpha = 0
	wallEnd1.material = matWall
	var wallSide1 = BABYLON.MeshBuilder.CreatePlane(
		"wallSide1",
		{ size: 50, width: 10, height: 9 },
		scene
	)
	wallSide1.checkCollisions = true
	wallSide1.material = matWall
	wallSide1.rotate(BABYLON.Axis.Y, Math.PI / 2, BABYLON.Space.WORLD)
	wallSide1.position.y = yPos
	wallSide1.position.x = 5
	var wallSide2 = wallSide1.createInstance("wallSide2")
	wallSide2.checkCollisions = true
	wallSide2.rotate(BABYLON.Axis.Y, -Math.PI, BABYLON.Space.WORLD)
	wallSide2.position.x = -5
	wallSide2.position.z = 0
	var wallEnd2 = wallEnd1.createInstance("wallEnd2")
	wallEnd2.checkCollisions = true
	wallEnd2.rotate(BABYLON.Axis.Y, Math.PI, BABYLON.Space.WORLD)
	wallEnd2.position.z = -5
}

function loadScene(onLoaded: () => void, onProgress: (value: number) => void) {
	BABYLON.SceneLoader.ShowLoadingScreen = false
	BABYLON.SceneLoader.Load(
		"assets/",
		"cat.babylon",
		engine,
		function (newScene) {
			newScene.executeWhenReady(async function () {
				scene = newScene
				window["scene" as any] = scene as any
				scene.clearColor = new BABYLON.Color4(0, 0, 0, 0)

				meshMain = scene.getMeshByName("body")
				setupLighting()
				setupFreeCamera()
				setupArcRotateCamera()
				Player.setup(engine)
				setupShadows()
				setupWorld()
				setupWalls()
				setupSkyBox()
				XRPointerInteractions.pannelsRotation(scene)
				xr = await scene.createDefaultXRExperienceAsync({
					uiOptions: { sessionMode: "immersive-ar" },
				})
				Global.scene = scene
				Global.xr = xr
				xr.baseExperience.sessionManager.onXRSessionInit.add(() => {
					meshUIVisibility(false)
					if (document.getElementById("customiseButton") != null) {
						document.getElementById("customiseButton")!.click()
					}
					document.getElementById("topRadio")!.click()
					Input.keysEnabled = false
					const canvasJoystick = document.getElementById("joystick")
					canvasJoystick!.style.zIndex = "-1"
					Player.xrPositon(xr)
					XRPointerInteractions.enable(scene, xr)
					HandMenu.enable(scene, xr)
					//XRPointerInteractions.pannelsAnchor(scene, xr)
				})

				xr.baseExperience.sessionManager.onXRSessionEnded.add(() => {
					meshUIVisibility(true)
					Input.keysEnabled = true
					const canvasJoystick = document.getElementById("joystick")
					canvasJoystick!.style.zIndex = "2"
					Player.centre()
				})
				toggleXRButtonVisibility()
				previewMode()

				var obsAfterRender: BABYLON.Nullable<BABYLON.Observer<BABYLON.Scene>> =
					null
				obsAfterRender = scene.onAfterRenderObservable.add(() => {
					if (scene.getMeshByName("ground")?.isReady(true)) {
						obsAfterRender!.unregisterOnNextCall = true
						onLoaded()
					}
				})
				engine.runRenderLoop(function () {
					scene.render()

					Player.update()
					if (cameraOption === "third") updateCamera()
					updateLight()
				})
				window.addEventListener("resize", function () {
					engine.resize()
				})
			})
		},
		(event: BABYLON.ISceneLoaderProgressEvent) => {
			const loaded = event.loaded
			const total = event.total
			const percentage = totalImages + (loaded / total) * (100 - totalImages)
			onProgress(percentage)
		}
	)
	if (!sceneLoaded) {
		sceneLoaded = true
	}
}

export function toggleXRButtonVisibility() {
	const xrButtons = document.getElementsByClassName("babylonVRicon")
	for (let i = 0; i < xrButtons.length; i++) {
		const xrButton = xrButtons[i] as HTMLElement // Type assertion to HTMLElement
		if (xrButton.style.display === "none") {
			xrButton.style.display = "block" // Or 'inline', depending on your layout
		} else {
			xrButton.style.display = "none"
		}
	}
}

function meshUIVisibility(toggle: boolean = false) {
	;(meshMain as BABYLON.Mesh).getScene().meshes.forEach(function (mesh) {
		if (
			mesh.id.startsWith("wall") ||
			mesh.id == "ground" ||
			mesh.id == "skyBox"
		) {
			mesh.isVisible = toggle // This hides the mesh
		}
		if (mesh.id.startsWith("UI_")) {
			mesh.isVisible = !toggle
		}
	})
}

function previewMode() {
	if (process.env.REACT_APP_VR_PREVIEW_FLAG == "true") {
		//document.getElementById("customiseButton")!.click()
		Input.keysEnabled = false
		scene.activeCamera = cameraFree
		cameraFree.position.x = 0
		cameraFree.position.y = Player.height + 1
		cameraFree.position.z = -0.5
		lookAt = new BABYLON.Vector3(0, 0.9 + Player.height, 0)
		cameraFree.setTarget(lookAt)
		cameraFree.detachControl()
		Input.keysEnabled = false
		Input.toggleSteeringWheel(false)
		const canvasJoystick = document.getElementById("joystick")
		canvasJoystick!.style.zIndex = "-1"
		Player.xrPositon()
		meshUIVisibility(false)
	}
}

export function Canvas({
	onLoaded,
	onProgress,
}: {
	onLoaded: () => void
	onProgress: (value: number) => void
}) {
	const canvasRef = useRef<HTMLCanvasElement>(null)
	useEffect(() => {
		engine = new BABYLON.Engine(canvasRef.current, true, {
			limitDeviceRatio: 1.0,
		})
		preloadImages(onProgress, () => {
			loadScene(onLoaded, onProgress)
		})
		if (process.env.REACT_APP_VR_PREVIEW_FLAG == "true") {
			canvasRef.current!.style.zIndex = "4"
		}
		return () => {
			engine.dispose()
		}
	}, [])

	return <canvas ref={canvasRef} id="myCanvas"></canvas>
}
